// Accounts
// ========

import { AppNotification } from './app-notification';
import { LBBContact } from 'sb-lib/types';
import { Segment } from './segment';

export enum SpotlightIntent {
  COLD = 'cold',
  WARM = 'warm',
  HOT = 'hot',
  ON_FIRE = 'on_fire',
}

export enum SpotlightTrend {
  DECREASING = 'decreasing',
  NEUTRAL = 'neutral',
  INCREASING = 'increasing',
  SURGING = 'surging',
}

export interface SpotlightAccount {
  id: number;
  name: string;
  domain: string;
  trend: SpotlightTrend;
  intent: SpotlightIntent;
  intentScore: number | null;
  // Stored sparsely and updated on demand, so when presenting a
  // histogram based on this list, iterate backwards for N days from
  // the current date and display any present values for those dates.
  intentScoreHistory: Record<string, number>;
  intentScoreTrend: number;
  sizeEstimateLow: number | null;
  sizeEstimateHigh: number | null;
  firstVisitDate: Date | null;
  lastVisitDate: Date | null;
  ownerId: number | null;
  clearbitData?: Record<string, any>;
  location?: string;
  revenue?: number;
  industry?: string;
  segments?: Segment[];
}

export interface SpotlightAccountsResponse {
  total: number;
  accounts: SpotlightAccount[];
}

export interface SpotlightAccountsIdsResponse {
  ids: number[];
}

export interface SpotlightCreateAccountRequest {
  name: string;
  domain: string;
}

export type SpotlightUTM =
  | 'utm_medium'
  | 'utm_source'
  | 'utm_campaign'
  | 'utm_content'
  | 'utm_term';

export interface SpotlightUTMRow {
  utmMedium?: string | null;
  utmSource?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  sessions: number;
}

export interface SpotlightUTMs {
  total: number;
  results: SpotlightUTMRow[];
}

// Account Lists
// =============

export interface SpotlightAccountListColumn {
  id: string;
  label: string;
  enabled: boolean;
}

export interface SpotlightAccountListUserSettings {
  columns: SpotlightAccountListColumn[];
}

export enum SpotlightAccountListKind {
  DYNAMIC = 'dynamic',
  STATIC = 'static',
}

export interface SpotlightAccountList {
  id: number;
  name: string;
  kind: SpotlightAccountListKind;
  filters: SpotlightFilter[] | null;
  userId: number | null;
}

export interface CreateDynamicSpotlightAccountListRequest {
  userId: number | null;
  name: string;
  kind: SpotlightAccountListKind.DYNAMIC;
  filters: SpotlightFilter[];
}

export interface CreateStaticSpotlightAccountListRequest {
  userId: number | null;
  name: string;
  kind: SpotlightAccountListKind.STATIC;
  accountIds: number[];
}

export type CreateSpotlightAccountListRequest =
  | CreateDynamicSpotlightAccountListRequest
  | CreateStaticSpotlightAccountListRequest;

export interface UpdateDynamicSpotlightAccountListRequest {
  userId?: number | null;
  name?: string;
  filters?: SpotlightFilter[];
}

export interface UpdateStaticSpotlightAccountListRequest {
  userId?: number | null;
  name?: string;
  accountIds?: number[];
}

export type UpdateSpotlightAccountListRequest =
  | UpdateDynamicSpotlightAccountListRequest
  | UpdateStaticSpotlightAccountListRequest;

// Contacts
// ========

export enum SpotlightContactSource {
  APOLLO = 'apollo',
  CHAT = 'chat',
  CLEARBIT = 'clearbit',
  DASHBOARD = 'dashboard',
  HUBSPOT = 'hubspot',
  INTERCOM = 'intercom',
  JOURNEY = 'journey',
  OUTREACH = 'outreach',
  SALESFORCE = 'salesforce',
  SALESLOFT = 'salesloft',
  SIXSENSE = 'sixsense',
  GOAL = 'goal',
  AI_CHAT = 'ai_chat',
}

export interface SpotlightContact extends LBBContact {
  segments?: Segment[];
  source: SpotlightContactSource;
}

type DotPrefix<T extends string> = T extends '' ? '' : `.${T}`;

type Leaf<T> = (T extends object ? keyof T : '') extends infer D
  ? Extract<D, string>
  : never;

export type Fields<T> = (
  T extends object
    ? {
        [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<Leaf<T[K]>>}`;
      }[Exclude<keyof T, symbol>]
    : ''
) extends infer D
  ? Extract<D, string>
  : never;

export interface SpotlightContactsResponse {
  total: number;
  contacts: SpotlightContact[];
}

export interface SpotlightContactsIdsResponse {
  ids: number[];
}

export interface SpotlightCreateContactRequest {
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
}

// Contact Lists
// =============

export interface SpotlightContactListColumn {
  id: string;
  label: string;
  enabled: boolean;
}

export interface SpotlightContactListUserSettings {
  columns: SpotlightContactListColumn[];
}

export enum SpotlightContactListKind {
  DYNAMIC = 'dynamic',
  STATIC = 'static',
}

export interface SpotlightContactList {
  id: number;
  name: string;
  kind: SpotlightContactListKind;
  filters: SpotlightFilter[] | null;
  userId: number | null;
}

export interface CreateDynamicSpotlightContactListRequest {
  userId: number | null;
  name: string;
  kind: SpotlightContactListKind.DYNAMIC;
  filters: SpotlightFilter[];
}

export interface CreateStaticSpotlightContactListRequest {
  userId: number | null;
  name: string;
  kind: SpotlightContactListKind.STATIC;
  contactIds: number[];
}

export type CreateSpotlightContactListRequest =
  | CreateDynamicSpotlightContactListRequest
  | CreateStaticSpotlightContactListRequest;

export interface UpdateDynamicSpotlightContactListRequest {
  userId?: number | null;
  name?: string;
  filters?: SpotlightFilter[];
}

export interface UpdateStaticSpotlightContactListRequest {
  userId?: number | null;
  name?: string;
  contactIds?: number[];
}

export type UpdateSpotlightContactListRequest =
  | UpdateDynamicSpotlightContactListRequest
  | UpdateStaticSpotlightContactListRequest;

// FilterMeta
// ==========

export interface SpotlightFilterMetaOperatorBase {
  kind: SpotlightFilterOperator;
}

export interface SpotlightFilterMetaOneOfOperator
  extends SpotlightFilterMetaOperatorBase {
  choices: { label: string; value: string }[];
}

export interface SpotlightFilterMetaRelatedInOperator
  extends SpotlightFilterMetaOperatorBase {
  choices: { label: string; value: string }[];
}

export interface SpotlightFilterMetaInRangeOperator
  extends SpotlightFilterMetaOperatorBase {
  choices: { label: string; lo: number; hi: number }[] | null;
}

export type SpotlightFilterMetaOperator =
  | SpotlightFilterMetaOperatorBase
  | SpotlightFilterMetaOneOfOperator
  | SpotlightFilterMetaInRangeOperator
  | SpotlightFilterMetaRelatedInOperator;

// Metadata about a thing that can be filtered on.
export interface SpotlightFilterMeta {
  label: string;
  field: string;
  kind: 'integer' | 'string' | 'datetime';
  operators: SpotlightFilterMetaOperator[];
}

export interface SpotlightPreferences {
  syncRevealedCompaniesToCrm: boolean;
}

// Intent Signals
// ==============

export enum SpotlightIntentSignalKind {
  ALERT = 'alert',
  DEAL = 'deal',
  ENGAGEMENT = 'engagement',
  MEETING_BOOKED = 'meeting_booked',
  PAGE_VIEW = 'page_view',
  SESSION_DURATION = 'session_duration',
}

export enum SpotlightIntentSignalStrength {
  HIGH = 'high',
  LOW = 'low',
}

export interface SpotlightIntentSignal {
  id: number;
  name: string;
  kind: SpotlightIntentSignalKind;
  active: boolean;
  strength: SpotlightIntentSignalStrength;
  filters: SpotlightFilter[];
  lastOccurrence: string;
}

export interface CreateSpotlightIntentSignalRequest {
  name: string;
  kind: SpotlightIntentSignalKind;
  active: boolean;
  strength: SpotlightIntentSignalStrength;
  filters: SpotlightFilter[];
}

// Filters
// =======

export enum SpotlightFilterOperator {
  AND = 'and',
  ASSIGNED_TO = 'assigned_to',
  CONTAINS = 'contains',
  IN_RANGE = 'in_range',
  IN_RELATIVE_DATE_RANGE = 'in_relative_date_range',
  NOT_EMPTY = 'not_empty',
  RELATED_NOT_EMPTY = 'related_not_empty',
  ONE_OF = 'one_of',
  OR = 'or',
  SEARCH = 'search',
  EQUALS = 'equals',
  RELATED_GREATER_THAN_OR_EQUAL = 'related_greater_than_or_equal',
  RELATED_ARRAY_LENGTH_GREATER_THAN_OR_EQUAL = 'related_array_length_greater_than_or_equal',
  RELATED_ARRAY_CONTAINS = 'related_array_contains',
  RELATED_ARRAY_CONTAINS_SUBSTRING = 'related_array_contains_substring',
  RELATED_IN = 'related_in',
  HAS_OPEN_DEAL = 'has_open_deal',
}

export interface SpotlightFilterAnd {
  operator: SpotlightFilterOperator.AND;
  operands: {
    filters: SpotlightFilter[];
  };
}

export interface SpotlightFilterAssignedTo {
  operator: SpotlightFilterOperator.ASSIGNED_TO;
  operands: {
    userId: number;
  };
}

export interface SpotlightFilterContains {
  operator: SpotlightFilterOperator.CONTAINS;
  operands: {
    field: string;
    substring: string;
  };
}

export interface SpotlightFilterEquals {
  operator: SpotlightFilterOperator.EQUALS;
  operands: {
    field: string;
    value: string;
  };
}

export interface SpotlightFilterRelatedGreaterOrEqualThan {
  operator: SpotlightFilterOperator.RELATED_GREATER_THAN_OR_EQUAL;
  operands: {
    field: string;
    value: number;
  };
}

export interface SpotlightFilterHasOpenDeal {
  operator: SpotlightFilterOperator.HAS_OPEN_DEAL;
  operands: {
    field: string;
    value: boolean;
  };
}

export interface SpotlightFilterRelatedArrayLengthGreaterOrEqualThan {
  operator: SpotlightFilterOperator.RELATED_ARRAY_LENGTH_GREATER_THAN_OR_EQUAL;
  operands: {
    field: string;
    value: number;
  };
}

export interface SpotlightFilterRelatedArrayContains {
  operator: SpotlightFilterOperator.RELATED_ARRAY_CONTAINS;
  operands: {
    field: string;
    value: string;
  };
}

export interface SpotlightFilterRelatedArrayContainsSubstring {
  operator: SpotlightFilterOperator.RELATED_ARRAY_CONTAINS_SUBSTRING;
  operands: {
    field: string;
    substring: string;
  };
}

export interface SpotlightFilterRelatedIn {
  operator: SpotlightFilterOperator.RELATED_IN;
  operands: {
    field: string;
    values: string[];
  };
}

export interface SpotlightFilterInRange {
  operator: SpotlightFilterOperator.IN_RANGE;
  operands: {
    field: string;
    lo: number;
    hi: number;
  };
}

export enum SpotlightFilterRelativeDateRange {
  PAST_DAY = 'past_day',
  PAST_WEEK = 'past_week',
  PAST_MONTH = 'past_month',
  PAST_YEAR = 'past_year',
}

export interface SpotlightFilterInRelativeDateRange {
  operator: SpotlightFilterOperator.IN_RELATIVE_DATE_RANGE;
  operands: {
    field: string;
    dateRange: SpotlightFilterRelativeDateRange;
  };
}

export interface SpotlightFilterNotEmpty {
  operator: SpotlightFilterOperator.NOT_EMPTY;
  operands: {
    field: string;
  };
}

export interface SpotlightFilterRelatedNotEmpty {
  operator: SpotlightFilterOperator.RELATED_NOT_EMPTY;
  operands: {
    field: string;
  };
}

export interface SpotlightFilterOneOf {
  operator: SpotlightFilterOperator.ONE_OF;
  operands: {
    field: string;
    values: string[];
  };
}

export interface SpotlightFilterOr {
  operator: SpotlightFilterOperator.OR;
  operands: {
    filters: SpotlightFilter[];
  };
}

export interface SpotlightFilterSearch {
  operator: SpotlightFilterOperator.SEARCH;
  operands: {
    q: string;
  };
}

export type SpotlightFilter =
  | SpotlightFilterAnd
  | SpotlightFilterAssignedTo
  | SpotlightFilterContains
  | SpotlightFilterInRange
  | SpotlightFilterInRelativeDateRange
  | SpotlightFilterOneOf
  | SpotlightFilterOr
  | SpotlightFilterSearch
  | SpotlightFilterEquals
  | SpotlightFilterRelatedGreaterOrEqualThan
  | SpotlightFilterRelatedArrayLengthGreaterOrEqualThan
  | SpotlightFilterRelatedArrayContains
  | SpotlightFilterRelatedArrayContainsSubstring
  | SpotlightFilterRelatedIn
  | SpotlightFilterHasOpenDeal
  | SpotlightFilterNotEmpty
  | SpotlightFilterRelatedNotEmpty;

export interface SpotlightAccountListAnalytics {
  totalAccountsInList: number;
  totalSurgingAccounts: number;
  totalCoolingAccounts: number;
  totalAlerts: number;
  topSurgingAccounts: SpotlightAccount[];
  topCoolingAccounts: SpotlightAccount[];
  recentAlerts: AppNotification[];
}
